import { useState } from 'react'
import { Link } from 'react-router-dom'
import { API_URL } from '../lib/api'
import { useUTM, getStoredUTM } from '../hooks/useUTM'

const LEARNING_POINTS = [
  'The exact carnivore protocol used by thousands to transform their health in 90 days',
  'Why conventional dieting advice keeps failing — and what to do instead',
  'How to combine fasting with carnivore for maximum results without the struggle',
  'The 3 biggest mistakes people make in their first 30 days and how to avoid them',
]

export default function Webinar() {
  useUTM()
  const [firstName, setFirstName] = useState('')
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) return

    setSubmitting(true)
    setError('')

    try {
      const utm = getStoredUTM()
      const res = await fetch(`${API_URL}/api/webinar/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          firstName: firstName || undefined,
          ...utm,
          visitor_id: localStorage.getItem('fc_visitor_id') || '',
        }),
      })

      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body.error || 'Something went wrong')
      }

      setSubmitted(true)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      {/* Hero */}
      <section style={{
        background: '#F2EDE6',
        padding: '6rem 1.5rem 3rem',
        textAlign: 'center',
      }}>
        <div style={{ maxWidth: '720px', margin: '0 auto' }}>
          <div style={{
            fontSize: '0.8rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            color: '#dc2626',
            marginBottom: '1rem',
          }}>
            Free Masterclass
          </div>
          <h1 style={{
            fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
            fontWeight: 500,
            color: '#0f172a',
            lineHeight: 1.3,
            marginBottom: '1rem',
          }}>
            The Carnivore Protocol That Changes Everything
          </h1>
          <p style={{
            fontSize: '1.1rem',
            color: '#555',
            lineHeight: 1.7,
            maxWidth: '560px',
            margin: '0 auto',
          }}>
            Learn the exact system used by thousands to transform their health in 90 days
          </p>
        </div>
      </section>

      {/* What You'll Learn + Registration */}
      <section style={{
        background: '#fff',
        padding: '4rem 1.5rem',
      }}>
        <div style={{
          maxWidth: '900px',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '3rem',
          alignItems: 'start',
        }}>
          {/* Left: What you'll learn */}
          <div>
            <h2 style={{
              fontSize: '1.35rem',
              fontWeight: 500,
              color: '#0f172a',
              marginBottom: '1.5rem',
            }}>
              What You'll Learn
            </h2>
            <ul style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '1.25rem',
            }}>
              {LEARNING_POINTS.map((point, i) => (
                <li
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.75rem',
                    fontSize: '0.95rem',
                    color: '#333',
                    lineHeight: 1.6,
                  }}
                >
                  <span style={{
                    flexShrink: 0,
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    background: '#fef2f2',
                    color: '#dc2626',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    marginTop: '0.1rem',
                  }}>
                    {i + 1}
                  </span>
                  {point}
                </li>
              ))}
            </ul>
          </div>

          {/* Right: Registration Form */}
          <div style={{
            background: '#faf9f7',
            border: '1px solid #e2e8f0',
            borderRadius: '5px',
            padding: '2.5rem 2rem',
          }}>
            {submitted ? (
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '50%',
                  background: '#dcfce7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1.5rem',
                  fontSize: '1.5rem',
                  color: '#16a34a',
                }}>
                  &#10003;
                </div>
                <h3 style={{
                  fontSize: '1.35rem',
                  fontWeight: 500,
                  color: '#0f172a',
                  marginBottom: '0.75rem',
                }}>
                  You're Registered
                </h3>
                <p style={{
                  color: '#555',
                  lineHeight: 1.7,
                  marginBottom: '1.5rem',
                }}>
                  Check your inbox for the masterclass details and access link. Add it to your calendar so you don't miss it.
                </p>
                <div style={{
                  background: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '5px',
                  padding: '1.25rem',
                  textAlign: 'left',
                }}>
                  <h4 style={{
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    color: '#0f172a',
                    marginBottom: '0.5rem',
                  }}>
                    What to expect:
                  </h4>
                  <ul style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem',
                  }}>
                    {[
                      'A confirmation email with your access link',
                      'The masterclass is approximately 60 minutes',
                      'Bring a notebook — you will want to take notes',
                      'Come with your biggest questions ready',
                    ].map((item, i) => (
                      <li key={i} style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.5rem',
                        fontSize: '0.85rem',
                        color: '#555',
                        lineHeight: 1.5,
                      }}>
                        <span style={{
                          flexShrink: 0,
                          width: '5px',
                          height: '5px',
                          borderRadius: '50%',
                          background: '#dc2626',
                          marginTop: '0.45rem',
                        }} />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <Link
                  to="/"
                  style={{
                    display: 'inline-block',
                    marginTop: '1.5rem',
                    color: '#dc2626',
                    textDecoration: 'none',
                    fontWeight: 500,
                    fontSize: '0.9rem',
                  }}
                >
                  &larr; Back to Fasting Carnivore
                </Link>
              </div>
            ) : (
              <>
                <h3 style={{
                  fontSize: '1.35rem',
                  fontWeight: 500,
                  color: '#0f172a',
                  marginBottom: '0.5rem',
                  textAlign: 'center',
                }}>
                  Reserve Your Spot
                </h3>
                <p style={{
                  color: '#555',
                  textAlign: 'center',
                  marginBottom: '1.5rem',
                  fontSize: '0.925rem',
                  lineHeight: 1.6,
                }}>
                  Enter your details below to get instant access.
                </p>

                <form onSubmit={handleSubmit} style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.875rem',
                }}>
                  <input
                    type="text"
                    placeholder="First name"
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                    style={{
                      padding: '0.875rem 1rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '5px',
                      fontSize: '1rem',
                      background: '#fff',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                    }}
                    onFocus={e => (e.currentTarget.style.borderColor = '#0f172a')}
                    onBlur={e => (e.currentTarget.style.borderColor = '#d1d5db')}
                  />
                  <input
                    type="email"
                    placeholder="Your email address"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    style={{
                      padding: '0.875rem 1rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '5px',
                      fontSize: '1rem',
                      background: '#fff',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                    }}
                    onFocus={e => (e.currentTarget.style.borderColor = '#0f172a')}
                    onBlur={e => (e.currentTarget.style.borderColor = '#d1d5db')}
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    style={{
                      padding: '0.975rem 1rem',
                      background: submitting ? '#999' : '#dc2626',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '5px',
                      fontSize: '1rem',
                      fontWeight: 500,
                      cursor: submitting ? 'not-allowed' : 'pointer',
                      transition: 'background 0.2s',
                    }}
                    onMouseEnter={e => {
                      if (!submitting) (e.currentTarget.style.background = '#b91c1c')
                    }}
                    onMouseLeave={e => {
                      if (!submitting) (e.currentTarget.style.background = '#dc2626')
                    }}
                  >
                    {submitting ? 'Reserving...' : 'Reserve My Spot'}
                  </button>
                  {error && (
                    <p style={{ color: '#dc2626', fontSize: '0.875rem', margin: 0 }}>{error}</p>
                  )}
                </form>

                <p style={{
                  fontSize: '0.825rem',
                  color: '#94a3b8',
                  marginTop: '1.25rem',
                  textAlign: 'center',
                  lineHeight: 1.5,
                }}>
                  Limited spots available. No spam. Unsubscribe anytime.
                </p>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section style={{
        background: '#F2EDE6',
        padding: '3rem 1.5rem',
        textAlign: 'center',
      }}>
        <p style={{
          fontSize: '0.8rem',
          color: '#999',
        }}>
          Results vary. No specific outcomes are guaranteed.
        </p>
      </section>
    </>
  )
}
